import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBluelagoon } from "@bluelagoon";
import MainLayout from "../Layouts/main";
import OnboardingChecklistItem from "./OnboardingChecklistItem";
import OnboardingGenerationProgress from "./OnboardingGenerationProgress";
import OnboardingRewardModal from "../components/OnboardingRewardModal";
import { toast } from "sonner";
import { Rocket, BookOpen, Coins, Users, Palette } from "lucide-react";

const TUTORIALS = [
    { key: "onboarding-create-workflow", icon: Rocket, credits: 10, i18n: "createApp" },
    { key: "onboarding-best-practices", icon: BookOpen, credits: 10, i18n: "bestPractices" },
    { key: "onboarding-credits-usage", icon: Coins, credits: 10, i18n: "creditsUsage" },
    { key: "onboarding-add-team-member", icon: Users, credits: 18, i18n: "addTeamMember" },
    { key: "onboarding-add-brand", icon: Palette, credits: 25, i18n: "addBrand" },
];

const LINKS = {
    bestPractices: { es: "https://www.youtube.com/watch?v=wzQwqupwU3k&t=19s", en: "https://help.agentui.ai/doc/best-practices-for-ai-chat" },
    credits: { es: "https://www.youtube.com/watch?v=qaVptG-Cw94", en: "https://help.agentui.ai/doc/credits" },
};

export default function Onboarding() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user, getUser } = useAuth();
    const [showRewardModal, setShowRewardModal] = useState(false);
    const { call: markComplete } = useBluelagoon({ method: "markTutorialComplete" });
    const lang = i18n.language?.startsWith("es") ? "es" : "en";
    const tutorials = user?.tutorials || {};
    const completedCount = useMemo(() => TUTORIALS.filter((t) => tutorials[t.key]?.completed).length, [tutorials]);
    const earnedCredits = useMemo(() => TUTORIALS.reduce((s, t) => s + (tutorials[t.key]?.completed ? t.credits : 0), 0), [tutorials]);

    const markAndRefresh = async (tutorialKey) => {
        const res = await markComplete({ tutorialKey });
        if (res?.creditsAwarded > 0) toast.success(`+${res.creditsAwarded} ${lang === "es" ? "créditos" : "credits"}!`);
        await getUser();
        if (res?.allOnboardingComplete) setShowRewardModal(true);
    };

    const handleAction = async (key) => {
        if (key === "onboarding-create-workflow") return navigate("/chat");
        if (key === "onboarding-best-practices") { window.open(LINKS.bestPractices[lang], "_blank", "noopener,noreferrer"); return markAndRefresh(key); }
        if (key === "onboarding-credits-usage") { window.open(LINKS.credits[lang], "_blank", "noopener,noreferrer"); return markAndRefresh(key); }
        if (key === "onboarding-add-team-member") return navigate("/profile/users");
        if (key === "onboarding-add-brand") return navigate("/profile/design-system");
    };

    useEffect(() => { getUser(); }, []);
    useEffect(() => {
        if (!user) return;
        if (!tutorials["onboarding-create-workflow"]?.completed && user.workflowCount > 0) markAndRefresh("onboarding-create-workflow");
    }, [user?.id]);

    return (
        <MainLayout>
            <div className="max-w-xl mx-auto py-8 px-4">
                <div className="text-center mb-6">
                    <h1 className="text-2xl font-bold">{t("onboarding.title")}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{t("onboarding.subtitle")}</p>
                    <div className="mt-3 flex items-center justify-center gap-4 text-sm">
                        <span className="font-medium">{t("onboarding.progress", { completed: completedCount, total: TUTORIALS.length })}</span>
                        {earnedCredits > 0 && <span className="text-amber-600 font-medium">{t("onboarding.creditsEarned", { credits: earnedCredits })}</span>}
                    </div>
                </div>
                <OnboardingGenerationProgress count={user?.company?.codeGenerationCount} />
                <div className="space-y-3">
                    {TUTORIALS.map((tut, idx) => (
                        <OnboardingChecklistItem
                            key={tut.key}
                            title={t(`onboarding.${tut.i18n}.title`)}
                            description={t(`onboarding.${tut.i18n}.description`)}
                            completed={!!tutorials[tut.key]?.completed}
                            onClick={() => handleAction(tut.key)}
                            icon={tut.icon}
                            credits={tut.credits}
                            highlight={idx === 0 && !tutorials[tut.key]?.completed}
                        />
                    ))}
                </div>
            </div>
            <OnboardingRewardModal open={showRewardModal} onOpenChange={setShowRewardModal} />
        </MainLayout>
    );
}
